import React, { useEffect, useRef, useState } from 'react';
import {Button, Col, DatePicker, Divider, Empty, Form, Popover, Row, Space, Table} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';

import {DownloadOutlined, EyeOutlined} from '@ant-design/icons';
import {getToken} from '../../../security';
import request from '../../../services/request';
import {serializeParams} from '../../../common/helpers';
import {apiUrl} from '../../../app.config';
import DownloadPdfIcon from '../../../common/components/DownloadPdfIcon';
import DownloadJsonIcon from '../../../common/components/DownloadJsonIcon';

const { RangePicker } = DatePicker;

export default function CertificateTable({ distributionPoint, history }) {
    const [loading, setLoading] = useState(false);
    const [certificates, setCertificates] = useState([]);
    const [pagination, setPagination] = useState({});
    const [form] = Form.useForm();
    const isFetching = useRef(false);

    const filters = {
        requestOCStartDate: dayjs().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        requestOCEndDate: dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        displayPage: 0,
        displayAmount: 10,
        distributionPointId: distributionPoint.id,
    };

    useEffect(() => {
        if (distributionPoint.id) {
            fetchCertificates(filters);
        }
    }, [distributionPoint]);

    const fetchCertificates = (params) => {
        setLoading(true);
        if (!distributionPoint.id || isFetching.current) return;
        isFetching.current = true;

        const queryParams = {
            page: params.displayPage,
            size: params.displayAmount,
            sort: 'folio,DESC',
        };

        request()
            .post(`operationCertificates${serializeParams(queryParams)}`, params)
            .then((response) => {
                setCertificates(response.data.content);
                setPagination({ ...pagination, total: response.data.totalElements });
            })
            .catch((error) => {
                console.error('Error fetching certificates:', error);
            })
            .finally(() => {
                setLoading(false);
                isFetching.current = false;
            });
    };

    const onChangePeriod = (period) => {
        const updatedFilters = {
            ...filters,
            requestOCStartDate: period[0].format('YYYY-MM-DD HH:mm:ss'),
            requestOCEndDate: period[1].format('YYYY-MM-DD HH:mm:ss'),
            distributionPointId: distributionPoint.id,
        };
        fetchCertificates(updatedFilters);
    };

    const columns = [
        { title: 'Folio', dataIndex: 'folio' },
        {
            title: 'Emisión del certificado',
            dataIndex: 'issuedCertificate',
            align: 'center',
            render: (date) => moment(date).format('YYYY/MM/DD')
        },
        {
            title: 'Inicio de verificación',
            dataIndex: 'starDateValidity',
            align: 'center',
            render: (date) => moment(date).format('YYYY/MM/DD')
        },
        {
            title: 'Fin de verificación',
            dataIndex: 'endDateValidity',
            align: 'center',
            render: (date) => moment(date).format('YYYY/MM/DD')
        },
        {
            title: 'Normativa',
            dataIndex: 'normativeCertificate'
        },
        {
            title: 'Resultado',
            dataIndex: 'result'
        },
        {
            title: 'Acciones',
            render: (text, record) => {
                const handleDownload = async (fileType) => {
                    const certificateId = record.id;
                    if (!certificateId) return;

                    try {
                        const url = `${apiUrl}operationCertificate/${certificateId}/${fileType}/download`;
                        const headers = {
                            Authorization: getToken(),
                            Accept: fileType === 'json' ? 'application/json' : '*/*'
                        };
                        let defaultFileName = `CE${new Date().toISOString().split('T')[0]}.${fileType}`;

                        const response = await fetch(url, {
                            method: 'GET',
                            headers: headers
                        });

                        if (!response.ok) {
                            throw new Error(`Error al descargar el archivo ${fileType.toUpperCase()}`);
                        }

                        const contentDisposition =
                            response.headers.get('Content-Disposition') ||
                            response.headers.get('content-disposition');
                        let fileName = defaultFileName;

                        if (contentDisposition) {
                            const match = contentDisposition.match(/filename="(.+)"/);
                            if (match && match[1]) {
                                fileName = match[1];
                            }
                        }

                        const blob = await response.blob();
                        const downloadUrl = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = downloadUrl;
                        a.download = fileName;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(downloadUrl);
                        document.body.removeChild(a);
                    } catch (error) {
                        console.error('Error en la descarga:', error);
                    }
                };

                return (
                    <Space>
                        <Popover content="Descargar JSON">
                            <Button icon={<DownloadJsonIcon />} onClick={() => handleDownload('json')} />
                        </Popover>
                        <Popover content="Descargar PDF">
                            <Button icon={<DownloadPdfIcon />} onClick={() => handleDownload('pdf')} />
                        </Popover>
                        <Popover>
                            <Button icon={<EyeOutlined />} onClick={() => history.push(`?edit=true&certificateId=${record.id}`)}/>
                        </Popover>
                    </Space>
                );
            }
        },
    ];

    return (
        <>
            <Form form={form} layout="vertical">
                <Row gutter={16} align="middle">
                    <Col xs={24} sm={16}>
                        <Form.Item
                            name="period"
                            label="Seleccionar periodo por fecha de certificado"
                            initialValue={[
                                dayjs().subtract(7, 'days').startOf('day'),
                                dayjs().endOf('day')
                            ]}
                            rules={[{ required: true, message: 'Por favor selecciona un periodo.' }]}
                        >
                            <RangePicker
                                lang="es"
                                format="YYYY/MM/DD"
                                style={{ width: 300 }}
                                onChange={onChangePeriod}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button type="primary" onClick={() => history.push('?add=true')}>
                            Agregar Certificado
                        </Button>
                    </Col>
                </Row>
            </Form>
            <Divider />
            <Table
                columns={columns}
                loading={loading}
                locale={{ emptyText: <Empty description="Sin registros" /> }}
                dataSource={certificates}
            />
        </>
    );
}
